import React, {useState} from 'react';

import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  connectionRequiresUserLogin,
  ConnectionStatus,
  InstrumentServiceDetails,
} from 'instruments/connections/ConnectionConstants';
import {InstrumentQueryStatus} from 'instruments/instrument-data/InstrumentDataConstants';
import * as InstrumentDataDAO from 'instruments/instrument-data/InstrumentDataDAO';
import InstrumentLoginPageModal from 'instruments/instrument-data/InstrumentLoginPageModal';
import {AutomationInputFileResponse} from 'lab-automation/automation-files/AutomationFileDAO';
import {runAutomationInputFileStatusModal} from 'lab-automation/automation-files/AutomationFileStatusModal';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import notifications from 'notifications';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {Optional} from 'util/Types';

import {AutomationInputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationInputFileGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import waitForEntrySaved from '../utils/waitForEntrySaved';
import {generateAutomationInputFile, setAutomationFileStatus} from './AutomationFileActions';
import * as AutomationFileDAO from './AutomationFileDAO';

interface GenerateAutomationInputFileButtonProps {
  runId: string | null;
  automationInputFile: AutomationInputFileGraphQL;
  isStale: boolean;
  disabled: boolean;
  hasOutputFileConfig: boolean;
  isConnectionRun: boolean;
  connectionId: string | null;
  instrumentServiceDetails?: InstrumentServiceDetails | null;
  inputFileStatus: AutomationFileStatusGraphQL | null;
  handleRefreshVersionedData: () => Promise<void>;
  updateAutomationFiles: (
    automationInputFile: AutomationInputFileGraphQL,
    automationOutputFile: AutomationOutputFileGraphQL | null
  ) => Promise<void>;
}

const GenerateAutomationInputFileButton: React.FunctionComponent<GenerateAutomationInputFileButtonProps> = (
  props: GenerateAutomationInputFileButtonProps
): JSX.Element => {
  const editorPropertiesContext = EditorPropertiesContext.useContext();

  const setStatusToRunning = (): void => {
    setAutomationFileStatus(props.automationInputFile.id, AutomationFileStatusGraphQL.running);
  };
  const setStatusToPending = (): void => {
    setAutomationFileStatus(props.automationInputFile.id, AutomationFileStatusGraphQL.pending);
  };
  const [isSendingToConnection, setIsSendingToConnection] = useState(false);
  const isRunning = props.inputFileStatus === AutomationFileStatusGraphQL.running;
  const generateFile = async (
    fileId: Optional<string>,
    copyToOutputFile: boolean,
    username?: string | null
  ): Promise<AutomationInputFileResponse | undefined> => {
    setStatusToRunning();
    try {
      const saved = fileId != null ? await waitForEntrySaved(fileId) : true;
      if (saved) {
        const response = await generateAutomationInputFile(
          props.runId!,
          props.automationInputFile.automationInputFileConfig.name,
          copyToOutputFile,
          username
        );
        await props.updateAutomationFiles(
          response.automationInputFile,
          response.automationOutputFile ?? null
        );
        if (response.errorMessage) {
          notifications.error(response.errorMessage, {duration: 20});
        }
        return response;
      }
    } catch (err) {
      setStatusToPending();
      throw err;
    }
    return undefined;
  };

  const dataTestAttrs = {component: 'GenerateAutomationInputFileButton', element: 'generateButton'};
  const isConnectionImportButton = props.isConnectionRun;
  const icon = isRunning ? <BenchlingIcon icon="refresh" className="glyphicon-spin" /> : 'refresh';

  const sharedButtonProps: ButtonProps = {
    autoHeight: true,
    className: 'processAutomationFileButton',
    buttonStyle: ButtonStyle.TERTIARY_GRAY,
    size: ButtonSize.MINI,
    fullWidth: true,
    disabled:
      props.disabled ||
      (isConnectionImportButton &&
        !isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_DISCOVER})),
    dataTestAttrs,
    icon,
  };

  // Generates the file and polls the instrument query for the send result.
  // Delays updateAutomationFiles until after polling completes so the UI stays
  // in a "running" state (showing "Sending to connection...") instead of briefly
  // flashing "Complete" before switching to "Failed" on error.
  const generateAndSendFile = async (username: string | null, copyToOutputFile: boolean): Promise<void> => {
    setStatusToRunning();
    try {
      const saved =
        editorPropertiesContext.fileId != null
          ? await waitForEntrySaved(editorPropertiesContext.fileId)
          : true;
      if (!saved) {
        setStatusToPending();
        return;
      }

      const response = await generateAutomationInputFile(
        props.runId!,
        props.automationInputFile.automationInputFileConfig.name,
        copyToOutputFile,
        username
      );

      if (response.errorMessage) {
        notifications.error(response.errorMessage, {duration: 20});
      }

      if (response.instrumentQueryId) {
        setIsSendingToConnection(true);
        // Re-assert running status periodically to prevent the live query refetch interval
        // in QueryingRunAutomationInputFileViewer from overwriting it to "succeeded"
        const statusKeepAlive = setInterval(() => {
          setStatusToRunning();
        }, 10000);
        try {
          const queryResponse = await InstrumentDataDAO.pollInstrumentQueryStatus(response.instrumentQueryId);
          if (queryResponse.status !== InstrumentQueryStatus.DONE) {
            await AutomationFileDAO.updateAutomationInputFileConnectionError(
              response.automationInputFile.id,
              queryResponse.info || 'Command failed'
            );
            await props.handleRefreshVersionedData();
            notifications.error(`Failed to send file to connection: ${queryResponse.info}`);
            return;
          }
        } catch (error) {
          if (error instanceof Error) {
            await AutomationFileDAO.updateAutomationInputFileConnectionError(
              response.automationInputFile.id,
              error.message
            );
            await props.handleRefreshVersionedData();
            notifications.error(`Failed to send file to connection: ${error.message}`);
            return;
          }
          throw error;
        } finally {
          clearInterval(statusKeepAlive);
          setIsSendingToConnection(false);
        }
      }

      // Only update the UI after the full operation (generate + send) completes successfully
      await props.updateAutomationFiles(response.automationInputFile, response.automationOutputFile ?? null);
    } catch (err) {
      setStatusToPending();
      throw err;
    }
  };

  const runInstrumentLoginPageModal = (copyToOutputFile: boolean): void => {
    legacyRunModal(InstrumentLoginPageModal, {
      // connectionId must be set for us to have instrumentServiceDetails, controlling whether we run this modal
      connectionId: props.connectionId!,
      onRequestClose: () => {},
      onRequestSubmit: (username: string | null) => {
        generateAndSendFile(username, copyToOutputFile);
      },
    });
  };

  if (props.isStale) {
    return (
      <Button
        {...sharedButtonProps}
        text={isRunning ? 'Generating - click to sync run' : 'Pending - click to sync run'}
        onClick={props.handleRefreshVersionedData}
      />
    );
  }

  if (isRunning) {
    return (
      <Button
        {...sharedButtonProps}
        text={isSendingToConnection ? 'Sending to connection...' : 'Generating...'}
        onClick={() => {
          runAutomationInputFileStatusModal({
            automationFile: props.automationInputFile,
            runId: props.runId,
            locked: props.disabled,
            editorPropertiesContext,
            updateAutomationInputFile: (updatedFile) => props.updateAutomationFiles(updatedFile, null),
          });
        }}
      />
    );
  }

  const connectionStatus = props.instrumentServiceDetails?.status.value;
  const requiresLogin = connectionRequiresUserLogin(props.instrumentServiceDetails);
  function onClickFunction(copyToOutputFile: boolean): void {
    if (requiresLogin) {
      runInstrumentLoginPageModal(copyToOutputFile);
    } else if (isConnectionImportButton) {
      generateAndSendFile(null, copyToOutputFile);
    } else {
      generateFile(editorPropertiesContext.fileId, copyToOutputFile);
    }
  }

  const isConnectionAvailable = !isConnectionImportButton || connectionStatus === ConnectionStatus.AVAILABLE;
  const isButtonDisabled =
    sharedButtonProps.disabled ||
    (!BenchlingConfig.INSTRUMENTS_ENABLE_OFFLINE_CONNECTION_DEMOS && !isConnectionAvailable);

  if (!props.hasOutputFileConfig) {
    return (
      <div>
        <Button
          {...sharedButtonProps}
          text={isConnectionImportButton ? 'Generate and send to connection' : 'Generate'}
          onClick={() => {
            onClickFunction(false);
          }}
          disabled={isButtonDisabled}
        />
      </div>
    );
  }

  const generateText = isConnectionImportButton ? 'Generate and send to connection' : 'Generate input file';
  const generateToOutputText = isConnectionImportButton
    ? 'Generate and send to connection and output'
    : 'Generate and send to output';
  return (
    <Dropdown
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
          <Option
            value={generateText}
            onSelect={() => {
              onClickFunction(false);
            }}
            dataTestAttrs={{
              component: 'GenerateAutomationInputFileButton',
              element: 'option',
              key: 'generate-input-file',
            }}
          >
            {generateText}
          </Option>
          <Option
            value={generateToOutputText}
            onSelect={() => {
              onClickFunction(true);
            }}
            dataTestAttrs={{
              component: 'GenerateAutomationInputFileButton',
              element: 'option',
              key: 'generate-and-send-to-output',
            }}
          >
            {generateToOutputText}
          </Option>
        </Menu>
      }
      allowMenuToCloseDropdown={false}
    >
      <Button {...sharedButtonProps} text="Generate" disabled={isButtonDisabled} />
    </Dropdown>
  );
};

export default GenerateAutomationInputFileButton;
